import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, Building2 } from "lucide-react";
import { Button, Field, Input, Textarea } from "@/design/components";
import { useSearchLeadContractorCandidates, useAssignLeadContractor } from "@/domain/useOnboarding";
import type { LeadContractorCandidate } from "@/domain/api";

// "Assign lead contractor" — for an organisation already active on Impact
// Ledger, skip the full invite/cascade-onboarding flow and attach it
// directly by searching its name. Structured like AddMembersModal.tsx
// (search-and-pick instead of free-text fields) but at the organisation
// level, and with a justification field since the backend endpoint records
// this as a governed action (matches EditProjectModal's direct-edit pattern)
// rather than a routine invite.
export function AssignLeadContractorModal({
  open,
  onOpenChange,
  projectId,
  projectName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number | string;
  projectName?: string;
}) {
  const { assignLeadContractor, isPending, error } = useAssignLeadContractor();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState<LeadContractorCandidate | null>(null);
  const [justification, setJustification] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Small local debounce — matches AddMembersModal's, no shared utility exists yet.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results = [], isFetching } = useSearchLeadContractorCandidates(projectId, debouncedQuery);

  const canSend = !!selected && justification.trim().length > 0;

  function reset() {
    setQuery("");
    setDebouncedQuery("");
    setSelected(null);
    setJustification("");
    setSubmitted(false);
  }

  async function onSend() {
    if (!selected) return;
    const result = await assignLeadContractor(projectId, {
      organisationId: selected.id,
      justification: justification.trim(),
    });
    if (result) setSubmitted(true);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted ? "Lead contractor assigned" : "Assign lead contractor"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>{selected?.name}</strong> is now the lead contractor for {projectName ?? "this project"}.
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                Attach an organisation already active on Impact Ledger as the lead contractor for {projectName ?? "this project"} — applies immediately, no invite needed.
              </Dialog.Description>

              <Field label="Search by organisation name">
                <div className="relative">
                  <Input
                    value={selected ? selected.name : query}
                    onChange={(e) => {
                      setSelected(null);
                      setQuery(e.target.value);
                    }}
                    placeholder="e.g. East African Wire & Cable Co."
                    autoComplete="off"
                  />
                  {!selected && debouncedQuery.trim().length >= 2 && (
                    <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-md border border-line bg-card shadow-overlay">
                      {isFetching ? (
                        <p className="px-3 py-2 text-sm text-faint">Searching…</p>
                      ) : results.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-faint">No matching active organisations.</p>
                      ) : (
                        results.map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-subtle"
                            onClick={() => {
                              setSelected(o);
                              setQuery("");
                            }}
                          >
                            <Building2 className="h-4 w-4 shrink-0 text-faint" aria-hidden />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-ink">{o.name}</p>
                              <p className="truncate text-xs text-faint">{o.email}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Justification">
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={2}
                  placeholder="Why is this organisation the right lead contractor?"
                />
              </Field>

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSend || isPending} onClick={onSend}>
                <Building2 className="h-4 w-4" /> {isPending ? "Assigning…" : "Assign"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
